// PORTFOLIO DATA — sourced from Pradeep Pabolu's resume

export const profile = {
  name: 'Pradeep Pabolu',
  role: 'Senior Cloud Solutions Architect',
  roles: ['Senior Cloud Solutions Architect', 'DevOps Architect', 'Enterprise Architect', 'Azure Expert', 'MCT'],
  tagline: 'Architecting Azure-first futures, one workload at a time.',
  bio: "Azure-focused Senior Cloud Solutions Architect with 20+ years of experience designing, migrating and optimizing enterprise cloud environments. Specialized in large-scale migrations from On-premises, AWS and GCP to Azure. SME in Azure Front Door, App Services, AKS, Landing Zones, IaC (Terraform / ARM / Bicep) and cloud governance.",
  location: 'India · US & UK',
  email: 'pradeep@pabolu.com',
  phone: '+91 97017 99050',
  photo: '/profile.jpg',
  socials: {
    linkedin: 'https://www.linkedin.com/in/pradeep-pabolu',
    github: 'https://github.com/pradeeppabolu',
    email: 'mailto:pradeep@pabolu.com'
  },
  yearsExperience: 20,
  projectsDelivered: 120,
  enterpriseClients: 50,
  certifications: 6
};

export const stats = [
  { value: '20+', label: 'Years of Experience', sub: 'Designing enterprise cloud systems' },
  { value: '120+', label: 'Projects Delivered', sub: 'On-prem · AWS · GCP → Azure' },
  { value: '50+', label: 'Enterprise Clients', sub: 'Fortune 500 to scale-ups' },
  { value: '99.99%', label: 'Avg. Uptime SLA', sub: 'Production-grade designs' }
];

export const techStack = [
  'Microsoft Azure', 'Azure Front Door', 'App Services', 'AKS',
  'Terraform', 'ARM / Bicep', 'Azure DevOps', 'GitHub Enterprise',
  'Azure Migrate', 'Azure Site Recovery', 'API Management', 'Key Vault',
  'App Gateway', 'Defender for Cloud', 'Entra ID', 'GitHub Copilot'
];

// Skill matrix (categorized) — used in About / Skills section
export const skillCategories = [
  {
    title: 'Cloud Architecture & Infrastructure',
    items: ['Azure Architecture', 'Landing Zones', 'CAF', 'ARM / Bicep', 'Terraform', 'Azure Migrate', 'ASR', 'Azure Networking', 'Entra ID', 'DNS', 'Azure Security', 'Backup Strategy']
  },
  {
    title: 'Azure PaaS Expertise (SME)',
    items: ['Azure Front Door', 'App Services', 'API Management', 'Key Vault', 'Storage', 'App Gateway']
  },
  {
    title: 'Cloud Migration',
    items: ['On-Prem → Azure', 'AWS → Azure', 'GCP → Azure', 'VM / App / DB Migrations', 'VMware → Azure', 'Hyper-V → Azure', 'VMware ↔ Hyper-V']
  },
  {
    title: 'Kubernetes & Cloud-Native',
    items: ['AKS', 'Helm', 'ACR', 'Microservices', 'Governance', 'Autoscaling']
  },
  {
    title: 'DevOps & IaC',
    items: ['Azure DevOps', 'GitHub', 'CI/CD', 'YAML pipelines', 'IaC automation', 'Policy-as-Code']
  },
  {
    title: 'Security & Compliance',
    items: ['IAM', 'RBAC', 'Defender for Cloud', 'Encryption', 'Network Security', 'SonarQube', 'Fortify']
  },
  {
    title: 'Presales & Client Engagement',
    items: ['Solution Design', 'Proposals', 'Workshops', 'Cloud Assessments']
  },
  {
    title: 'Cost Optimization',
    items: ['Azure Cost Management', 'FinOps', 'Budgeting', 'Right-sizing']
  }
];

export const experience = [
  {
    period: 'Nov 2024 — Present',
    role: 'Practice Lead — Cloud & Modern DevOps',
    company: 'Infomagnus',
    location: 'India',
    summary: 'Lead the cloud and modern DevOps practice — owning architecture, presales, delivery and post-migration excellence for enterprise customers.',
    points: [
      'Architected Azure solutions using Azure Front Door, App Services, App Gateway, Key Vault and secure network designs.',
      'Led On-prem → Azure migrations using Landing Zones, identity governance and IaC provisioning.',
      'Conducted presales workshops, solution proposals and cloud assessments.',
      'Implemented CI/CD, IaC standards and Kubernetes governance.',
      'Delivered post-migration tuning, security hardening and cost optimization.'
    ]
  },
  {
    period: 'Jan 2024 — Nov 2024',
    role: 'Manager — Azure Cloud & DevOps',
    company: 'Envoy Global',
    location: 'India',
    summary: 'Managed Azure architecture and migration programs — taking AWS-hosted workloads to a unified Azure platform.',
    points: [
      'Designed Azure architectures using App Services, Azure Front Door, Key Vault and enterprise networks.',
      'Led AWS → Azure migrations including assessment, re-platforming and cutover.',
      'Built Terraform / ARM templates for PaaS and IaaS deployments.',
      'Implemented AKS governance and Helm repositories.',
      'Strengthened cloud security through RBAC, Conditional Access and Defender for Cloud.',
      'Supported presales solutioning for Azure migration engagements.'
    ]
  },
  {
    period: 'Sep 2019 — Dec 2023',
    role: 'Manager — Cloud Architecture & Operations',
    company: 'Harsco Corporation',
    location: 'India',
    summary: 'Drove enterprise cloud transformation — earning the CEO Impact Award and multiple cloud transformation awards.',
    points: [
      'Architected On-prem → Azure migrations, multi-region platforms and Landing Zones.',
      'Designed PaaS architectures using App Services, Front Door, App Gateway and AKS modernization.',
      'Led DR strategy, cost optimization and enterprise cloud governance.',
      'Managed AKS clusters, Helm charts and secure container pipelines.',
      'Received CEO Impact Award + multiple cloud transformation awards.'
    ]
  },
  {
    period: 'Sep 2012 — Sep 2019',
    role: 'Principal Consultant — Azure Cloud',
    company: 'Neudesic Technologies',
    location: 'India',
    summary: 'Senior consulting role spanning architecture, presales and delivery for large enterprises adopting Azure.',
    points: [
      'Architected Azure PaaS / IaaS solutions with App Services, Front Door and scalable enterprise designs.',
      'Led migration assessments and strategies for large customers.',
      'Built Terraform / ARM automation and DevOps pipelines.',
      'Delivered presales proposals, workshops and PoCs.',
      'Mentored teams on Azure readiness and cloud engineering best practices.'
    ]
  },
  {
    period: 'Earlier',
    role: 'Windows / AD Administration & Enterprise Support',
    company: 'CA Technologies (TEK Systems) · Broadridge Financial Services · C3i Support Services',
    location: 'India',
    summary: 'Foundational years building deep operating-system, identity and enterprise-support expertise — the bedrock for two decades of architecture work.',
    points: [
      'Windows Server & Active Directory administration at enterprise scale.',
      'Endpoint security, patching and server operations.',
      'Enterprise application support across regulated industries.'
    ]
  }
];

export const projects = [
  {
    id: 1,
    title: 'Global Azure Front Door Architecture',
    category: 'Networking',
    tags: ['Front Door', 'WAF', 'Routing', 'Secure Delivery'],
    summary: 'Designed global Azure Front Door architectures with WAF, intelligent routing and secure delivery for multi-region enterprise applications.',
    impact: 'Multi-region · WAF protected',
    accent: '#219EBC'
  },
  {
    id: 2,
    title: 'App Services Modernization',
    category: 'Modernization',
    tags: ['App Services', 'Slots', 'Auto-scale', 'Security'],
    summary: 'Migrated workloads to Azure App Services with proper environment separation, deployment slots, scaling rules and security baselines.',
    impact: 'IaaS → PaaS · Zero-downtime cutover',
    accent: '#023047'
  },
  {
    id: 3,
    title: 'AWS → Azure Migration',
    category: 'Migration',
    tags: ['Azure Migrate', 'Re-platform', 'ASR', 'Cutover'],
    summary: 'Executed AWS → Azure migration covering assessment, re-platforming, identity, network and cutover for an enterprise estate.',
    impact: 'Full estate moved · No prod outage',
    accent: '#219EBC'
  },
  {
    id: 4,
    title: 'On-Prem → Azure Migration',
    category: 'Migration',
    tags: ['Azure Migrate', 'ASR', 'Landing Zone', 'Identity'],
    summary: 'Re-platformed on-prem workloads to Azure using Azure Migrate, ASR and Landing Zones with hybrid identity and governance.',
    impact: 'Hybrid identity · Governed at scale',
    accent: '#0c4a6e'
  },
  {
    id: 5,
    title: 'Enterprise Landing Zones',
    category: 'Cloud Strategy',
    tags: ['CAF', 'Landing Zone', 'Governance', 'Compliance'],
    summary: 'Built CAF-aligned Landing Zones with identity, governance, networking and compliance layers — ready for regulated workloads.',
    impact: 'Audit-ready · Policy-as-Code',
    accent: '#023047'
  },
  {
    id: 6,
    title: 'IaC Automation — Terraform / ARM / Bicep',
    category: 'DevOps',
    tags: ['Terraform', 'ARM', 'Bicep', 'CI/CD'],
    summary: 'Developed Terraform, ARM and Bicep templates for automated, repeatable, policy-compliant Azure provisioning.',
    impact: 'Click-ops eliminated · 100% IaC',
    accent: '#219EBC'
  },
  {
    id: 7,
    title: 'Disaster Recovery & High Availability',
    category: 'Resilience',
    tags: ['ASR', 'Cross-region', 'Failover', 'BCDR'],
    summary: 'Designed cross-region DR and HA strategies with replication, failover orchestration and tested runbooks.',
    impact: 'RTO < 1h · 100% test pass',
    accent: '#023047'
  },
  {
    id: 8,
    title: 'AKS Governance & Modernization',
    category: 'Kubernetes',
    tags: ['AKS', 'Helm', 'ACR', 'Governance'],
    summary: 'Stood up production-grade AKS with Helm-based deployments, ACR-backed images, autoscaling and security guardrails.',
    impact: 'Microservices @ scale',
    accent: '#0c4a6e'
  },
  {
    id: 9,
    title: 'Cloud Security Hardening',
    category: 'Security',
    tags: ['Defender', 'RBAC', 'Conditional Access', 'Zero Trust'],
    summary: 'Strengthened cloud security with RBAC, Conditional Access, Defender for Cloud and network microsegmentation.',
    impact: 'Zero-trust posture',
    accent: '#219EBC'
  },
  {
    id: 10,
    title: 'CI/CD Pipelines — Azure DevOps & GitHub',
    category: 'DevOps',
    tags: ['Azure DevOps', 'GitHub Actions', 'YAML', 'Policy-as-Code'],
    summary: 'Standardized CI/CD across teams using Azure DevOps and GitHub Actions with YAML pipelines and policy gates.',
    impact: 'Deploys: weekly → daily',
    accent: '#023047'
  },
  {
    id: 11,
    title: 'Cost Optimization & FinOps',
    category: 'FinOps',
    tags: ['Cost Management', 'Right-sizing', 'Reservations', 'Budgets'],
    summary: 'Established a FinOps practice with showback, right-sizing, reservations and continuous anomaly detection.',
    impact: 'Significant TCO reduction',
    accent: '#219EBC'
  },
  {
    id: 12,
    title: 'Presales — Workshops & Cloud Assessments',
    category: 'Advisory',
    tags: ['Workshops', 'Proposals', 'Assessments', 'PoCs'],
    summary: 'Led presales solutioning, workshops, cloud assessments and PoCs for enterprise migration engagements.',
    impact: 'Wins: $multi-million pipeline',
    accent: '#0c4a6e'
  }
];

export const projectCategories = ['All', 'Migration', 'Cloud Strategy', 'Modernization', 'DevOps', 'Kubernetes', 'Security', 'Resilience', 'FinOps', 'Networking', 'Advisory'];

export const certifications = [
  { name: 'Microsoft Certified: Azure Solutions Architect Expert', code: 'AZ-305', issuer: 'Microsoft', badge: '/badges/az305.png' },
  { name: 'Microsoft Certified: Azure Security Engineer Associate', code: 'AZ-500', issuer: 'Microsoft', badge: '/badges/az500.png' },
  { name: 'Microsoft Certified: DevOps Engineer Expert', code: 'AZ-400', issuer: 'Microsoft', badge: '/badges/az400.png' },
  { name: 'Microsoft Certified Trainer (MCT)', code: 'MCT', issuer: 'Microsoft', badge: '/badges/mct.png' },
  { name: 'GitHub Administrator & Copilot Expert', code: 'GH-ADM', issuer: 'GitHub', badge: null },
  { name: 'ITIL Foundation', code: 'ITIL-F', issuer: 'PeopleCert', badge: null }
];

export const achievements = [
  { title: 'CEO Impact Award', year: 'Harsco Corp.', detail: 'Recognized for leading enterprise cloud transformation and measurable business impact.' },
  { title: 'Multiple Cloud Transformation Awards', year: 'Harsco Corp.', detail: 'Honored across multiple years for migration, modernization and governance excellence.' },
  { title: 'Microsoft Certified Trainer (MCT)', year: 'Active', detail: 'Active MCT — designing and delivering enterprise Azure training programs.' },
  { title: 'GitHub Copilot Expert', year: 'Active', detail: 'Accredited GitHub Administrator & Copilot Expert — enabling enterprise Copilot rollouts.' },
  { title: 'International Engagements', year: 'US · UK', detail: 'Multiple onsite engagements across the United States and United Kingdom.' },
  { title: 'Practice Lead — Cloud & DevOps', year: '2024 — Now', detail: 'Leading Cloud & Modern DevOps practice at Infomagnus.' }
];

export const services = [
  { title: 'Cloud Strategy & Landing Zones', desc: 'CAF-aligned Landing Zones with identity, governance, networking and compliance layers — ready for regulated workloads.' },
  { title: 'Migrations — On-Prem · AWS · GCP → Azure · VMware ↔ Hyper-V', desc: 'Specialist in large-scale migrations using Azure Migrate, ASR and re-platforming for VM, app and database workloads — including VMware → Hyper-V and Hyper-V → VMware cross-hypervisor migrations.' },
  { title: 'Azure PaaS — Front Door, App Services, AKS', desc: 'SME design for global Front Door architectures, App Services modernization and production-grade AKS.' },
  { title: 'Infrastructure as Code', desc: 'Terraform, ARM and Bicep templates with CI/CD policy gates. Repeatable, reviewed, audited — no more click-ops.' },
  { title: 'Security & Identity', desc: 'IAM, RBAC, Defender for Cloud, Conditional Access and network security baselines for a zero-trust posture.' },
  { title: 'FinOps & Cost Optimization', desc: 'Azure Cost Management, budgets, right-sizing and reservations — predictable spend without sacrificing performance.' }
];
